import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useGraphQL } from "../../context/GraphQLProvider";
import DashboardMainContainer from "../../components/common/Containers/DashboardMainContainer";
import DashboardAdsCard from "../../components/common/AdsCard/DashboardAdsCard";
import Alerts from "../../components/common/Alerts/Alerts";
import {
  AdminPageTitle,
  AdminSupportingText,
  AdminHeaderAction,
} from "../../sections/AdminDashboard/shared/adminUx";
import {
  CUSTOMER_CENTERED_PAGE_TITLE_CLASS,
  CUSTOMER_POST_GRID_CLASS,
  CUSTOMER_LIST_FOOTER_CLASS,
} from "../../sections/CustomerDashboard/shared/customerLayoutSpec";

const SavedPosts = () => {
  const { user: userId } = useSelector((state) => state.user);
  const graphqlService = useGraphQL();
  const [allSavedPosts, setAllSavedPosts] = useState([]);
  const [displayedPosts, setDisplayedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [removingPostId, setRemovingPostId] = useState(null);
  const navigate = useNavigate();

  const fetchSavedPosts = async () => {
    if (userId) {
      setIsLoading(true);
      try {
        // TODO: Replace with actual GraphQL query for saved posts
        // const response = await GetSavedPosts(graphqlService, {
        //   userId: parseFloat(userId.id)
        // });
        // const posts = response?.getSavedPosts || [];
        
        // Dummy data for testing
        const posts = [
          {
            id: 1,
            postTitle: "Professional Plumbing Services",
            postDescription: "Expert plumbing services for residential and commercial properties. 24/7 emergency service available.",
            mainCategory: "Plumbing",
            subCategory: "General Plumbing",
            content: JSON.stringify([
              { original: require("../../assets/img/P1.jpeg"), thumbnail: require("../../assets/img/P1.jpeg") },
              { original: require("../../assets/img/P2.jpeg"), thumbnail: require("../../assets/img/P2.jpeg") }
            ]),
            userId: 1,
            postKeywords: ["plumbing", "emergency", "repairs"],
            postRadius: "10km",
            postSuburb: "Sydney CBD",
            isHighlighted: true,
            likes: 45,
            views: 320
          },
          {
            id: 2,
            postTitle: "Quality Electrical Work",
            postDescription: "Licensed electricians providing safe and reliable electrical services.",
            mainCategory: "Electrical",
            subCategory: "Residential Electrical",
            content: JSON.stringify([
              { original: require("../../assets/img/P3.jpeg"), thumbnail: require("../../assets/img/P3.jpeg") }
            ]),
            userId: 2,
            postKeywords: ["electrical", "licensed", "safe"],
            postRadius: "15km",
            postSuburb: "Parramatta",
            isHighlighted: false,
            likes: 32,
            views: 215
          },
          {
            id: 3,
            postTitle: "Expert Carpentry Services",
            postDescription: "Custom carpentry and woodwork for your home or business.",
            mainCategory: "Carpentry",
            subCategory: "Custom Work",
            content: JSON.stringify([
              { original: require("../../assets/img/P4.jpeg"), thumbnail: require("../../assets/img/P4.jpeg") },
              { original: require("../../assets/img/P5.jpeg"), thumbnail: require("../../assets/img/P5.jpeg") }
            ]),
            userId: 3,
            postKeywords: ["carpentry", "custom", "woodwork"],
            postRadius: "20km",
            postSuburb: "Bondi",
            isHighlighted: false,
            likes: 28,
            views: 180
          },
          {
            id: 4,
            postTitle: "Professional Painting Services",
            postDescription: "Interior and exterior painting for homes and offices.",
            mainCategory: "Painting",
            subCategory: "Residential Painting",
            content: JSON.stringify([
              { original: require("../../assets/img/P77.jpg"), thumbnail: require("../../assets/img/P77.jpg") }
            ]),
            userId: 4,
            postKeywords: ["painting", "interior", "exterior"],
            postRadius: "25km",
            postSuburb: "Manly",
            isHighlighted: true,
            likes: 52,
            views: 410
          },
          {
            id: 5,
            postTitle: "Landscaping & Garden Design",
            postDescription: "Transform your outdoor space with professional landscaping services.",
            mainCategory: "Landscaping",
            subCategory: "Garden Design",
            content: JSON.stringify([
              { original: require("../../assets/img/P1.jpeg"), thumbnail: require("../../assets/img/P1.jpeg") }
            ]),
            userId: 5,
            postKeywords: ["landscaping", "garden", "design"],
            postRadius: "30km",
            postSuburb: "Chatswood",
            isHighlighted: false,
            likes: 38,
            views: 265
          }
        ];
        
        setAllSavedPosts(posts);
        // Show first 9 posts by default
        setDisplayedPosts(posts.slice(0, 9));
      } catch (error) {
        console.error("Error fetching saved posts:", error);
        Alerts.error("Failed to fetch saved posts. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchSavedPosts();
  }, []);

  const handleLoadMore = () => {
    setIsLoadingMore(true);
    // Simulate loading delay for better UX
    setTimeout(() => {
      const currentCount = displayedPosts.length;
      const nextBatch = allSavedPosts.slice(currentCount, currentCount + 6);
      setDisplayedPosts(prev => [...prev, ...nextBatch]);
      setIsLoadingMore(false);
    }, 500);
  };

  const handleViewPost = (postId) => {
    navigate(`/service-detail/?id=53`);
  };

  const handleRemoveFromSaved = async (postId) => {
    setRemovingPostId(postId);
    try {
      // TODO: Replace with actual GraphQL mutation to remove from saved
      // await RemoveFromSavedMutation(graphqlService, {
      //   userId: parseFloat(userId.id),
      //   postId: postId
      // });
      
      // Remove from both arrays
      setAllSavedPosts(prev => prev.filter(post => post.id !== postId));
      setDisplayedPosts(prev => prev.filter(post => post.id !== postId));
      Alerts.success("Post removed from saved.");
    } catch (error) {
      console.error("Error removing post from saved:", error);
      Alerts.error("We couldn't remove this post. Please try again later.");
    } finally {
      setRemovingPostId(null);
    }
  };

  const hasMorePosts = displayedPosts.length < allSavedPosts.length;

  if (isLoading) {
    return (
      <DashboardMainContainer className="pb-10 !bg-white">
        <AdminPageTitle className={CUSTOMER_CENTERED_PAGE_TITLE_CLASS}>
          Saved Posts
        </AdminPageTitle>
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primaryblue"></div>
        </div>
      </DashboardMainContainer>
    );
  }

  return (
    <DashboardMainContainer className="pb-10 !bg-white">
      <AdminPageTitle className={CUSTOMER_CENTERED_PAGE_TITLE_CLASS}>
        Saved Posts
      </AdminPageTitle>

      {displayedPosts?.length === 0 ? (
        <div className="text-center">
          <i className="fi fi-rs-heart text-6xl text-mediumgray mb-4" aria-hidden />
          <AdminSupportingText className="text-offblack block">No saved posts yet</AdminSupportingText>
          <AdminSupportingText className="text-mediumgray mt-2 block">
            Posts you save will appear here
          </AdminSupportingText>
        </div>
      ) : (
        <div>
          <div className={CUSTOMER_POST_GRID_CLASS}>
            {displayedPosts.map((post) => (
              <div 
                key={post.id} 
                className={`relative ${removingPostId === post.id ? 'opacity-50 pointer-events-none' : ''}`}
              >
                {removingPostId === post.id && (
                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primaryblue"></div>
                  </div>
                )}
                <DashboardAdsCard
                  saveButton={false}
                  additionalActions={true}
                  editButton={false}
                  editOnClick={() => handleViewPost(post.id)}
                  viewButton={true}
                  viewOnClick={() => handleViewPost(post.id)}
                  deleteButton={true}
                  deleteOnClick={() => handleRemoveFromSaved(post.id)}
                  postTitle={post.postTitle}
                  serviceDescription={post.postDescription}
                  category={post.mainCategory}
                  subCategory={post.subCategory}
                  Images={post.content ? JSON.parse(post.content) : []}
                  serviceId={post.id}
                  companyId={post.userId}
                  postKeywords={post.postKeywords}
                  postRadius={post.postRadius}
                  postSuburb={post.postSuburb}
                  isHighlighted={post.isHighlighted}
                  likes={post.likes}
                  views={post.views}
                />
              </div>
            ))}
          </div>

          {/* Load More Button */}
          {hasMorePosts && (
            <div className={CUSTOMER_LIST_FOOTER_CLASS}>
              <AdminHeaderAction onClick={handleLoadMore} disabled={isLoadingMore}>
                {isLoadingMore ? "Loading..." : "Load more"}
              </AdminHeaderAction>
            </div>
          )}
        </div>
      )}
    </DashboardMainContainer>
  );
};

export default SavedPosts;

